import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { analytics, EVENTS } from '../../lib/analytics'
import {
  Box, Typography, Button, Card, Stack, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress, FormControlLabel, Switch, Alert, Autocomplete,
} from '@mui/material'
import { Add, Delete, LocationOn, Timeline, RadioButtonUnchecked, Campaign } from '@mui/icons-material'
import { MapContainer, Polygon, Circle, FeatureGroup, useMap } from 'react-leaflet'
import MapTileLayer from '../../components/MapTileLayer'
import { EditControl } from 'react-leaflet-draw'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet-draw/dist/leaflet.draw.css'
import { useGeofences, useCreateGeofence, useDeleteGeofence } from '../../hooks/useFleet'
import { useAudiences, useSubjectSubscriptions, useCreateSubscription, useDeleteSubscription } from '../../hooks/useAudiences'
import { usePermissions, PERMS } from '../../hooks/usePermissions'

type DrawMode = 'polygon' | 'circle'

/** Programmatically activates the polygon or circle draw tool on the map. */
function DrawStarter({ mode, active }: { mode: DrawMode; active: boolean }) {
  const map = useMap()

  useEffect(() => {
    if (!active || !map) return

    // Small delay to ensure the map is fully rendered inside the dialog
    const timer = setTimeout(() => {
      const Draw = (L as any).Draw
      const handler = mode === 'polygon'
        ? new Draw.Polygon(map, { shapeOptions: { color: '#34A8E0', weight: 2, fillOpacity: 0.15 } })
        : new Draw.Circle(map, { shapeOptions: { color: '#34A8E0', weight: 2, fillOpacity: 0.15 } })
      handler.enable()
    }, 200)

    return () => clearTimeout(timer)
  }, [map, mode, active])

  return null
}

export default function GeofencesPage() {
  const { t } = useTranslation()
  const { hasPermission } = usePermissions()
  const canManageAudiences = hasPermission(PERMS.MANAGE_NOTIFICATION_ROUTING)
  const { data: list = [], isLoading } = useGeofences()
  const create = useCreateGeofence()
  const remove = useDeleteGeofence()

  const [open, setOpen] = useState(false)
  const [audienceTarget, setAudienceTarget] = useState<string | null>(null)
  const [drawMode, setDrawMode] = useState<DrawMode>('polygon')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [color, setColor] = useState('#34A8E0')
  const [drawnShape, setDrawnShape] = useState<{ shape: string; geometry: any } | null>(null)
  const [notifyDriver, setNotifyDriver] = useState(false)
  const [drawKey, setDrawKey] = useState(0)

  const openWithMode = useCallback((mode: DrawMode) => {
    setDrawMode(mode)
    setOpen(true)
    setDrawKey((k) => k + 1)
  }, [])

  const handleClose = useCallback(() => {
    setOpen(false)
    setDrawnShape(null)
  }, [])

  const handleDrawCreated = (e: any) => {
    const layer = e.layer
    const layerType = e.layerType

    if (layerType === 'circle') {
      const center = layer.getLatLng()
      const radius = layer.getRadius()
      setDrawnShape({
        shape: 'circle',
        geometry: { center: [center.lat, center.lng], radius_m: Math.round(radius) },
      })
    } else if (layerType === 'polygon' || layerType === 'rectangle') {
      const latlngs = layer.getLatLngs()[0]
      const coords = latlngs.map((ll: any) => [ll.lat, ll.lng])
      setDrawnShape({
        shape: 'polygon',
        geometry: { coords },
      })
    }
  }

  const handleCreate = async () => {
    if (!drawnShape || !name) return
    try {
      await create.mutateAsync({
        name,
        description,
        shape: drawnShape.shape as 'circle' | 'polygon',
        geometry: drawnShape.geometry,
        color,
        is_active: true,
        notify_driver: notifyDriver,
      } as any)
      analytics.track(EVENTS.GEOFENCE_CREATED, { geofence_name: name, shape: drawnShape.shape })
      setOpen(false)
      setDrawnShape(null)
      setName('')
      setDescription('')
      setNotifyDriver(false)
    } catch {
      // Error toast shown by API interceptor
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress sx={{ color: '#34A8E0' }} />
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#0f172a" sx={{ letterSpacing: '-0.02em' }}>
            {t('geofences.title')}
          </Typography>
          <Typography variant="body2" color="#94a3b8" sx={{ mt: 0.5 }}>
            {t('geofences.zoneCount', { count: list.length })}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<Timeline />}
            onClick={() => openWithMode('polygon')}
            sx={{
              background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)',
              borderRadius: 1.5,
              fontWeight: 600,
              '&:hover': { background: 'linear-gradient(135deg,#2B8BB9,#1f6f96)' },
            }}
          >
            {t('geofences.addPolygon', { defaultValue: 'Add Polygon' })}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RadioButtonUnchecked />}
            onClick={() => openWithMode('circle')}
            sx={{
              borderColor: '#34A8E0',
              color: '#34A8E0',
              borderRadius: 1.5,
              fontWeight: 600,
              '&:hover': { borderColor: '#2B8BB9', bgcolor: '#EBF6FD' },
            }}
          >
            {t('geofences.addCircle', { defaultValue: 'Add Circle' })}
          </Button>
        </Stack>
      </Box>

      {(create.isError || remove.isError) && (
        <Alert severity="error" sx={{ mb: 2 }}>{t('common.error')}</Alert>
      )}

      <Stack spacing={1.5}>
        {list.length === 0 && (
          <Card sx={{ p: 6, textAlign: 'center', border: '1px solid #e2e8f0', borderRadius: 2 }}>
            <LocationOn sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
            <Typography variant="body2" color="#64748b">{t('geofences.noGeofences')}</Typography>
          </Card>
        )}
        {list.map((g) => {
          const geo = g.geometry as any
          return (
            <Card key={g.id} sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: g.color }} />
                  <Box>
                    <Typography variant="body1" fontWeight={700} color="#1e293b">{g.name}</Typography>
                    <Typography variant="caption" color="#94a3b8">
                      {g.shape}
                      {g.shape === 'circle' && geo?.radius_m && ` · ${geo.radius_m}m ${t('geofences.radius')}`}
                      {g.shape === 'polygon' && geo?.coords && ` · ${t('geofences.pointCount', { count: geo.coords.length })}`}
                      {' · '}{g.is_active ? 'active' : 'inactive'}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip label={g.shape} size="small" variant="outlined" />
                  {canManageAudiences && (
                    <Tooltip title={t('geofences.assignAudience')}>
                      <IconButton size="small" onClick={() => setAudienceTarget(g.id)}
                        sx={{ color: '#94a3b8', '&:hover': { color: '#34A8E0' } }}>
                        <Campaign fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={t('common.delete')}>
                    <IconButton size="small" onClick={() => { remove.mutate(g.id) }}
                      sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                      <Delete sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Card>
          )
        })}
      </Stack>

      {/* Create geofence dialog with map drawing */}
      <Dialog open={open} onClose={handleClose}
        maxWidth="md" fullWidth disableEnforceFocus
        PaperProps={{ sx: { borderRadius: 2.5, border: '1px solid #e2e8f0', height: '80vh' } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          {drawMode === 'polygon' ? <Timeline sx={{ color: '#34A8E0' }} /> : <RadioButtonUnchecked sx={{ color: '#34A8E0' }} />}
          {drawMode === 'polygon'
            ? t('geofences.drawPolygon', { defaultValue: 'Draw Polygon Geofence' })
            : t('geofences.drawCircle', { defaultValue: 'Draw Circle Geofence' })
          }
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1, overflow: 'hidden' }}>
          <Stack direction="row" spacing={1.5}>
            <TextField label={t('common.name')} required size="small" value={name} fullWidth
              onChange={(e) => setName(e.target.value)} />
            <TextField label={t('common.description')} size="small" value={description} fullWidth
              onChange={(e) => setDescription(e.target.value)} />
            <TextField label={t('geofences.color')} size="small" value={color} sx={{ minWidth: 120 }}
              onChange={(e) => setColor(e.target.value)}
              InputProps={{ startAdornment: <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: color, mr: 1 }} /> }}
            />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              control={<Switch checked={notifyDriver} onChange={(e) => setNotifyDriver(e.target.checked)} />}
              label={t('geofences.notifyDriver')}
            />
            {/* Switch draw mode inside dialog */}
            <Stack direction="row" spacing={0.5} sx={{ ml: 'auto' }}>
              <Button
                size="small"
                variant={drawMode === 'polygon' ? 'contained' : 'outlined'}
                startIcon={<Timeline sx={{ fontSize: '16px !important' }} />}
                onClick={() => { setDrawMode('polygon'); setDrawnShape(null); setDrawKey((k) => k + 1) }}
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  borderRadius: 1,
                  ...(drawMode === 'polygon'
                    ? { background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)', '&:hover': { background: 'linear-gradient(135deg,#2B8BB9,#1f6f96)' } }
                    : { borderColor: '#e2e8f0', color: '#64748b', '&:hover': { borderColor: '#34A8E0', color: '#34A8E0' } }
                  ),
                }}
              >
                {t('geofences.polygon', { defaultValue: 'Polygon' })}
              </Button>
              <Button
                size="small"
                variant={drawMode === 'circle' ? 'contained' : 'outlined'}
                startIcon={<RadioButtonUnchecked sx={{ fontSize: '16px !important' }} />}
                onClick={() => { setDrawMode('circle'); setDrawnShape(null); setDrawKey((k) => k + 1) }}
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  borderRadius: 1,
                  ...(drawMode === 'circle'
                    ? { background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)', '&:hover': { background: 'linear-gradient(135deg,#2B8BB9,#1f6f96)' } }
                    : { borderColor: '#e2e8f0', color: '#64748b', '&:hover': { borderColor: '#34A8E0', color: '#34A8E0' } }
                  ),
                }}
              >
                {t('geofences.circle', { defaultValue: 'Circle' })}
              </Button>
            </Stack>
          </Stack>
          {drawnShape ? (
            <Chip
              label={t('geofences.shapeDrawn', { shape: drawnShape.shape })}
              color="success" size="small" sx={{ alignSelf: 'flex-start' }}
            />
          ) : (
            <Alert severity="info" sx={{ py: 0.5, fontSize: 13 }}>
              {drawMode === 'polygon'
                ? t('geofences.polygonHint', { defaultValue: 'Click on the map to place polygon points. Click the first point to close the shape.' })
                : t('geofences.circleHint', { defaultValue: 'Click and drag on the map to draw a circle.' })
              }
            </Alert>
          )}
          <Box sx={{ flex: 1, borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            <MapContainer key={drawKey} center={[33.3152, 44.3661]} zoom={11} style={{ height: '100%', width: '100%' }}>
              <MapTileLayer />
              <FeatureGroup>
                <EditControl
                  position="topright"
                  onCreated={handleDrawCreated}
                  draw={{
                    rectangle: false,
                    polyline: false,
                    marker: false,
                    circlemarker: false,
                    polygon: false,
                    circle: false,
                  }}
                />
              </FeatureGroup>
              <DrawStarter mode={drawMode} active={open} />
              {/* Show existing geofences for reference */}
              {list.map((g) => {
                const geo = g.geometry as any
                if (g.shape === 'polygon' && geo?.coords?.length) {
                  return <Polygon key={g.id} positions={geo.coords as [number, number][]}
                    pathOptions={{ color: g.color || '#34A8E0', weight: 2, fillOpacity: 0.1 }} />
                }
                if (g.shape === 'circle' && geo?.center && geo?.radius_m) {
                  return <Circle key={g.id} center={geo.center as [number, number]}
                    radius={geo.radius_m} pathOptions={{ color: g.color || '#34A8E0', weight: 2, fillOpacity: 0.1 }} />
                }
                return null
              })}
            </MapContainer>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleClose} sx={{ color: '#64748b' }}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleCreate}
            disabled={!name || !drawnShape || create.isPending}
            sx={{ background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)', borderRadius: 1.5 }}>
            {t('geofences.createGeofence')}
          </Button>
        </DialogActions>
      </Dialog>

      {audienceTarget && (
        <AudienceAssignDialog geofenceId={audienceTarget} onClose={() => setAudienceTarget(null)} />
      )}
    </Box>
  )
}

export function AudienceAssignDialog({ geofenceId, onClose }: { geofenceId: string; onClose: () => void }) {
  const { t } = useTranslation()
  const { data: groups = [] } = useAudiences()
  const subsQuery = useSubjectSubscriptions('geofence', geofenceId)
  const subs = subsQuery.data ?? []
  const createSub = useCreateSubscription()
  const deleteSub = useDeleteSubscription()
  const subscribedGroupIds = subs.filter((s) => s.group_id).map((s) => s.group_id as string)

  // The control is disabled whenever the cache isn't guaranteed to reflect the
  // server: while a create/delete is in flight, AND while the subscription
  // list is (re)fetching — which covers the invalidation-triggered refetch a
  // mutation kicks off. Deriving `subscribedGroupIds` purely from `subs` (no
  // local optimistic override) means every diff is computed against confirmed
  // server state, so a delete can never be silently dropped for lack of a
  // subscription row id (only a round-tripped create produces one) and a
  // rapid re-selection can never re-fire a duplicate create.
  const isBusy = createSub.isPending || deleteSub.isPending || subsQuery.isFetching

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('geofences.assignAudience')}</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('geofences.audienceHint')}
        </Typography>
        <Box sx={{ position: 'relative' }}>
          <Autocomplete
            multiple size="small" disableCloseOnSelect
            disabled={isBusy}
            options={groups}
            getOptionLabel={(g) => g.name}
            value={groups.filter((g) => subscribedGroupIds.includes(g.id))}
            onChange={(_, selected) => {
              const selectedIds = new Set(selected.map((g) => g.id))
              for (const g of selected)
                if (!subscribedGroupIds.includes(g.id))
                  createSub.mutate({ subject_type: 'geofence', subject_id: geofenceId, event_type: 'zone_entry', group_id: g.id })
              for (const s of subs)
                if (s.group_id && !selectedIds.has(s.group_id)) deleteSub.mutate({ id: s.id, subject_type: 'geofence', subject_id: geofenceId })
            }}
            renderInput={(params) => <TextField {...params} label={t('audiences.title')} />}
          />
          {isBusy && (
            <CircularProgress
              size={16}
              sx={{ position: 'absolute', top: '50%', insetInlineEnd: 36, transform: 'translateY(-50%)' }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  )
}
